import { mapGetters, mapState } from 'vuex';
import { Capacitor } from '@capacitor/core';
import { decideDownloadQuality } from '@/services/download/downloadQualityPrompt';
import { askQuality, askConfirm, askCellular, askModules } from '@/services/download/downloadSheets';

// Fluxo de download de UMA aula, compartilhado por quem oferece esse gesto na UI
// (hoje: o ícone no menu lateral, o botão na página da aula e a página Meus
// Downloads). Baixar não é um dispatch e pronto — passa por sheet de qualidade,
// confirmação de dados móveis e confirmação de remoção/cancelamento. Duplicar
// isso por tela garante que as telas divirjam no primeiro ajuste.
//
// As sheets são renderizadas UMA vez pelo <DownloadSheets /> no AppLayout; aqui
// só perguntamos e damos await na resposta. Nenhum consumidor precisa montar
// nada no próprio template.
export default {
  computed: {
    // Prefixo `dl` pra não colidir com nomes que os consumidores já mapeiam por
    // conta própria — se colidisse, a versão do componente venceria e o mixin
    // passaria a depender de algo que ele não controla.
    ...mapGetters({
      dlConnectionType: 'dom/connectionType',
      dlIsConnected: 'dom/isConnected',
      dlDownloadOnCellular: 'download/getDownloadOnCellular',
      dlCellularConfirmedThisCycle: 'download/isCellularConfirmedThisCycle',
      dlLoStatus: 'download/getLearningObjectStatus',
      dlLoDownloaded: 'download/isLearningObjectDownloaded',
      dlLoProgress: 'download/getEffectiveProgress',
      dlEnrollSummary: 'download/getEnrollDownloadSummary'
    }),
    // Estes são state cru, não getter.
    ...mapState({
      dlQualityWifi: (state) => state.download.downloadQualityWifi,
      dlQualityLastCellular: (state) => state.download.downloadQualityLastCellular,
      dlDrmConfig: (state) => state.learningObject.drm
    })
  },

  methods: {
    // Uma aula é baixável quando: (a) estamos no app nativo (offline depende do
    // filesystem/plugin, não existe no web), (b) o produtor não desabilitou
    // download pro curso, (c) ela tem conteúdo baixável — não é só quiz/link
    // externo/vídeo de provider externo — e (d) se for PDF, o visualizador não
    // está bloqueado por DRM.
    canDownloadLo (lo, courseId) {
      if (!lo || !this.$isApp) return false;

      const cfg = this.dlDrmConfig;
      const drmMatchesCourse = !!(cfg && cfg.codCurso == courseId);
      if (drmMatchesCourse && cfg.desabilitarDownloadAulas === true) return false;

      const isExternalVideo = !!(lo.broadcast && lo.broadcast.isExternal);
      const hasDownloadable = !!(
        (lo.broadcast && !isExternalVideo) || lo.pdf || lo.htmlContent || lo.audio
      );
      const isExclusivelyOnlineOnly =
        !hasDownloadable && !!(lo.forms || lo.externalContent || isExternalVideo);
      if (isExclusivelyOnlineOnly) return false;

      if (lo.pdf && drmMatchesCourse) return !cfg.desabilitarDownloadVisualizador;
      return true;
    },

    // Decide o que o clique faz a partir do estado atual da aula:
    //   queued/downloading → cancela (apaga os parciais do disco)
    //   downloaded         → remove
    //   idle               → baixa (qualidade + cellular antes)
    // `lo` é o objeto da aula (precisa de `broadcast` pra saber se é vídeo).
    async toggleLoDownload ({ enrollId, lo, name }) {
      if (!enrollId || !lo) return;
      const id = lo.id;
      const itemName = name || lo.title || lo.name || null;
      const status = this.dlLoStatus(enrollId, id);

      if (status === 'downloading' || status === 'queued') {
        if (await this.confirmCancelDownload(itemName)) {
          this.$store.dispatch('download/removeLearningObject', { enrollId, id });
        }
        return;
      }

      if (this.dlLoDownloaded(enrollId, id)) {
        if (await this.confirmRemoveDownload(itemName)) {
          this.$store.dispatch('download/removeLearningObject', { enrollId, id });
        }
        return;
      }

      if (!(await this.ensureCellularAllowed(itemName))) return;

      const quality = await this.resolveDownloadQuality({ hasVideo: !!lo.broadcast });
      if (quality === null) return; // desistiu na sheet de qualidade
      this.$store.dispatch('download/downloadLearningObject', { enrollId, id, quality });
    },

    // Versão de CURSO do toggleLoDownload. A diferença de gesto: baixar não
    // dispara direto — abre a sheet de módulos primeiro. Curso inteiro pode ser
    // dezenas de GB, e escolher o escopo antes é o que separa "baixei o que
    // queria" de "o app comeu meu armazenamento".
    //
    // Remover/cancelar seguem sem seleção: são uma decisão só, e oferecer
    // checkbox pra desfazer seria pedir duas confirmações pro mesmo arrependimento.
    async toggleEnrollDownload ({ enrollId, groups, courseName, courseId, filesCount }) {
      if (!enrollId || !groups || !groups.length) return;

      const loIds = groups.reduce((acc, g) => acc.concat(g.learningObjectIds || []), []);
      const { status } = this.dlEnrollSummary(enrollId, loIds);
      const removeAll = () => this.$store.dispatch('download/removeAllFromEnroll', { enrollId });

      // A sheet abre em TODOS os estados, inclusive baixando e baixado. Antes,
      // esses dois iam direto para a confirmação de cancelar/remover — e como no
      // app a sheet é o único caminho para os materiais complementares (a linha
      // "Downloads" do resumo fica escondida lá), o aluno perdia acesso a eles
      // exatamente quando terminava de baixar o curso.
      //
      // O que era o significado implícito do toque virou ação explícita no
      // rodapé da aba "Aulas": baixar, cancelar ou remover, conforme o estado.
      const result = await askModules(groups, { courseId, filesCount, status });
      if (!result) return;

      if (result.action === 'cancel') {
        if (await this.confirmCancelDownload(courseName)) removeAll();
        return;
      }

      if (result.action === 'remove') {
        if (await this.confirmRemoveDownload(courseName)) removeAll();
        return;
      }

      const chosen = result.groups;
      if (!chosen || !chosen.length) return;

      if (!(await this.ensureCellularAllowed(courseName))) return;

      const hasVideo = chosen.some(g => g.hasVideo);
      const quality = await this.resolveDownloadQuality({ hasVideo });
      if (quality === null) return;

      this.$store.dispatch('download/downloadEnroll', { enrollId, groups: chosen, quality });
    },

    // → 'low' | 'medium' | 'high' | null. Não pergunta quando já há padrão
    // aplicável (ver decideDownloadQuality).
    async resolveDownloadQuality ({ hasVideo }) {
      const decision = decideDownloadQuality({
        hasVideo,
        isNative: Capacitor.isNativePlatform(),
        isCellular: this.dlConnectionType === 'cellular',
        wifiQuality: this.dlQualityWifi,
        lastCellularQuality: this.dlQualityLastCellular
      });

      if (decision.decision !== 'prompt') return decision.quality;

      const quality = await askQuality(decision);
      if (quality === null) return null;

      // Persiste a escolha CONFORME o modo (efeito colateral declarativo):
      //   - firstTime: vira o padrão Wi-Fi do app (a partir daqui não pergunta
      //     mais em Wi-Fi até user mexer em Meus Downloads ou voltar pra null).
      //   - cellular: salva como "última escolha cellular" pra pré-seleção da
      //     próxima fila cellular. NÃO mexe no padrão Wi-Fi.
      //   - editSetting: vira o padrão Wi-Fi (mesma mutation, contexto diferente).
      if (decision.mode === 'firstTime' || decision.mode === 'editSetting') {
        this.$store.commit('download/SET_DOWNLOAD_QUALITY_WIFI', quality);
      } else if (decision.mode === 'cellular') {
        this.$store.commit('download/SET_DOWNLOAD_QUALITY_LAST_CELLULAR', quality);
      }
      return quality;
    },

    // → true se pode seguir. Pergunta APENAS quando: (a) está em cellular,
    // (b) user não optou por "sempre baixar em cellular" (persistido), (c) a
    // fila atual ainda não recebeu confirmação dessa sessão. Cliques sucessivos
    // durante a mesma fila entram direto; quando a fila zera, a flag reseta.
    async ensureCellularAllowed (itemName) {
      const needsAsk = this.dlConnectionType === 'cellular'
        && !this.dlDownloadOnCellular
        && !this.dlCellularConfirmedThisCycle;
      if (!needsAsk) return true;

      const answer = await askCellular(itemName);
      if (!answer) return false;
      await this.$store.dispatch('download/confirmCellularDownloadInThisCycle', {
        persist: answer.persist
      });
      return true;
    },

    // Abre a sheet de qualidade FORA do fluxo de download, pra editar o padrão
    // Wi-Fi (Meus Downloads → Configurações). Não passa pelo decideDownloadQuality
    // porque aqui o usuário pediu explicitamente pra escolher.
    async editDefaultQuality (currentQuality) {
      const quality = await askQuality({
        mode: 'editSetting',
        initialSelected: currentQuality || 'medium'
      });
      if (quality === null) return null;
      this.$store.commit('download/SET_DOWNLOAD_QUALITY_WIFI', quality);
      return quality;
    },

    // → true se confirmou. Remoção de conteúdo já baixado.
    confirmRemoveDownload (itemName) {
      return askConfirm({ itemName });
    },

    // → true se confirmou. Cancelamento de download em andamento: mesma sheet,
    // outros textos — o que está em jogo é diferente (interrompe e descarta o
    // parcial, em vez de apagar algo que o usuário já tinha completo).
    confirmCancelDownload (itemName) {
      return askConfirm({
        itemName,
        icon: 'cancel',
        title: 'Cancelar o download?',
        description: 'O que já foi baixado desse conteúdo será apagado do dispositivo.',
        dismissLabel: 'Continuar baixando',
        confirmLabel: 'Cancelar download',
        confirmIcon: 'stop'
      });
    }
  }
};
